import { AddOnRateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isNil } from 'lodash-es';

import { AutocompleteFieldOption } from '~components/FormFields/v2/Autocomplete/types';
import { AddOnCharge, AddOnTypeahead } from '~hooks/useAddOns/models';
import { InvoiceLineItem } from '~hooks/useInvoiceLineItems/models';
import { Invoice } from '~hooks/useInvoices/models';
import { SettlementLinesUpdate } from '~hooks/useSettlementLines/useSettlementLines';
import { Settlement } from '~hooks/useSettlements/models';
import {
  AddOnChargeDTO,
  isLineEditable,
  schemaHelpers,
} from '~pages/Approvals/DriverPay/helpers';
import { Nullable } from '~types/Nullable';
import { toDecimal } from '~utils/decimal';

import { getAddOnChargeRate, SettlementSheetLines } from './settlementSheetLines';

export interface SettlementLineDraft {
  quantity: number | string;
  rate: number | string;
}

export interface NewSettlementAddOnChargeDraft {
  addOn: AutocompleteFieldOption<AddOnTypeahead> | null;
  isPercentage: boolean;
  prefilledAddOnId: string | null;
  quantity: number | string;
  rate: number | string;
}

export interface SettlementSheetFormValues {
  addOnCharges: Record<string, SettlementLineDraft>;
  lineItems: Record<string, SettlementLineDraft>;
  newAddOnCharges: NewSettlementAddOnChargeDraft[];
  notes: string;
}

export const EMPTY_NEW_ADD_ON_CHARGE: NewSettlementAddOnChargeDraft = {
  addOn: null,
  isPercentage: false,
  prefilledAddOnId: null,
  quantity: 1,
  rate: 0,
};

export const draftNumber = (value: number | string) => {
  const number = Number(value);

  return Number.isFinite(number) ? number : 0;
};

export const isDraftChanged = (
  draft: SettlementLineDraft | undefined,
  quantity: number,
  rate: Nullable<number>,
) =>
  draft !== undefined &&
  (!toDecimal(draftNumber(draft.quantity)).equals(quantity) ||
    (!isNil(rate) && !toDecimal(draftNumber(draft.rate)).equals(rate)));

export const isPercentageDraft = ({
  addOn,
  isPercentage,
}: NewSettlementAddOnChargeDraft) =>
  isPercentage || addOn?._entity.addOnRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL;

const canEditInvoiceLine = (
  settlement: Settlement,
  line: { editable: boolean | null },
  invoice: Invoice | undefined,
) => settlement.editable && invoice !== undefined && isLineEditable(line, invoice);

export const canEditSettlementAddOnCharge = (
  settlement: Settlement,
  addOnCharge: AddOnCharge,
) => settlement.editable && addOnCharge.editable !== false;

const getSheetLineItems = ({ orderGroups }: SettlementSheetLines) =>
  orderGroups.flatMap(({ charges }) => charges.flatMap(({ lineItems }) => lineItems));

const getSheetInvoiceAddOnCharges = ({ orderGroups }: SettlementSheetLines) =>
  orderGroups.flatMap(({ charges, jobAddOnCharges }) => [
    ...charges.flatMap(({ addOnCharges }) => addOnCharges),
    ...jobAddOnCharges,
  ]);

const toLineDraft = (quantity: number, rate: Nullable<number>) => ({
  quantity,
  rate: rate ?? 0,
});

export function getSettlementSheetFormValues(
  settlement: Settlement,
  lines: SettlementSheetLines,
  settlementAddOnCharges: AddOnCharge[],
): SettlementSheetFormValues {
  const editableLineItems = getSheetLineItems(lines).filter((lineItem) =>
    canEditInvoiceLine(settlement, lineItem, lines.invoiceByLineId.get(lineItem.id)),
  );
  const editableAddOnCharges = [
    ...getSheetInvoiceAddOnCharges(lines).filter((addOnCharge) =>
      canEditInvoiceLine(
        settlement,
        addOnCharge,
        lines.invoiceByLineId.get(addOnCharge.id ?? ''),
      ),
    ),
    ...settlementAddOnCharges.filter((addOnCharge) =>
      canEditSettlementAddOnCharge(settlement, addOnCharge),
    ),
  ].filter((addOnCharge) => !isNil(getAddOnChargeRate(addOnCharge)));

  return {
    addOnCharges: Object.fromEntries(
      editableAddOnCharges.map((addOnCharge) => [
        addOnCharge.id ?? '',
        toLineDraft(addOnCharge.quantity, getAddOnChargeRate(addOnCharge)),
      ]),
    ),
    lineItems: Object.fromEntries(
      editableLineItems.map((lineItem) => [
        lineItem.id,
        toLineDraft(lineItem.quantity, lineItem.rate),
      ]),
    ),
    newAddOnCharges: [],
    notes: settlement.notes ?? '',
  };
}

const isValidAmount = (value: number | string) =>
  value !== '' && Number.isFinite(Number(value));

export const getAmountError = (value: number | string) =>
  isValidAmount(value) ? undefined : t('form_validation_errors.should_be_number_simple');

const isValidLineDraft = ({ quantity, rate }: SettlementLineDraft) =>
  isValidAmount(quantity) && isValidAmount(rate);

const isValidNewAddOnChargeDraft = (draft: NewSettlementAddOnChargeDraft) =>
  draft.addOn !== null &&
  draft.addOn.value === draft.prefilledAddOnId &&
  isValidLineDraft(draft);

const pickDrafts = (drafts: Record<string, SettlementLineDraft>, ids: string[]) =>
  ids.flatMap((id) => (id in drafts ? [drafts[id]] : []));

export const areSettlementSheetDraftsValid = (
  lines: SettlementSheetLines,
  settlementAddOnCharges: AddOnCharge[],
  { addOnCharges, lineItems, newAddOnCharges }: SettlementSheetFormValues,
) => {
  const addOnChargeIds = [
    ...getSheetInvoiceAddOnCharges(lines),
    ...settlementAddOnCharges,
  ].flatMap(({ id }) => (id ? [id] : []));
  const lineItemIds = getSheetLineItems(lines).map(({ id }) => id);

  return (
    [
      ...pickDrafts(addOnCharges, addOnChargeIds),
      ...pickDrafts(lineItems, lineItemIds),
    ].every(isValidLineDraft) && newAddOnCharges.every(isValidNewAddOnChargeDraft)
  );
};

const toAddOnChargeDTO = (addOnCharge: AddOnCharge, draft: SettlementLineDraft) => ({
  ...schemaHelpers.addOnChargeToAddOnChargeDTO(addOnCharge),
  quantity: draftNumber(draft.quantity),
  value: draftNumber(draft.rate),
});

const toNewAddOnChargeDTO = ({
  addOn,
  isPercentage,
  quantity,
  rate,
}: NewSettlementAddOnChargeDraft): AddOnChargeDTO[] =>
  addOn
    ? [
        {
          addOn: { id: addOn._entity.id, name: addOn._entity.name },
          addOnRateType: addOn._entity.addOnRateType,
          isPercentage,
          jobId: null,
          load: {},
          quantity: draftNumber(quantity),
          rebillValue: 0,
          surchargeType: null,
          value: draftNumber(rate),
        },
      ]
    : [];

const isAddOnChargeDraftChanged = (
  addOnCharge: AddOnCharge,
  draft: SettlementLineDraft | undefined,
): draft is SettlementLineDraft =>
  isDraftChanged(draft, addOnCharge.quantity, getAddOnChargeRate(addOnCharge));

const appendTo = <Item>(itemsByKey: Map<string, Item[]>, key: string, item: Item) =>
  itemsByKey.set(key, [...(itemsByKey.get(key) ?? []), item]);

export function getSettlementLinesUpdate(
  lines: SettlementSheetLines,
  settlementAddOnCharges: AddOnCharge[],
  values: SettlementSheetFormValues,
) {
  const lineItemsByInvoiceId = new Map<string, Array<Partial<InvoiceLineItem>>>();
  const addOnChargesByInvoiceId = new Map<string, AddOnChargeDTO[]>();

  getSheetLineItems(lines).forEach((lineItem) => {
    const draft = values.lineItems[lineItem.id];

    if (draft && isDraftChanged(draft, lineItem.quantity, lineItem.rate)) {
      appendTo(lineItemsByInvoiceId, lineItem.invoiceId, {
        id: lineItem.id,
        quantity: draftNumber(draft.quantity),
        rate: isNil(lineItem.rate) ? null : draftNumber(draft.rate),
      });
    }
  });

  getSheetInvoiceAddOnCharges(lines).forEach((addOnCharge) => {
    const draft = values.addOnCharges[addOnCharge.id ?? ''];
    const invoiceId = lines.invoiceByLineId.get(addOnCharge.id ?? '')?.id;

    if (invoiceId && isAddOnChargeDraftChanged(addOnCharge, draft)) {
      appendTo(addOnChargesByInvoiceId, invoiceId, toAddOnChargeDTO(addOnCharge, draft));
    }
  });

  return {
    addOnChargesByInvoiceId,
    lineItemsByInvoiceId,
    settlementAddOnCharges: [
      ...settlementAddOnCharges.flatMap((addOnCharge) => {
        const draft = values.addOnCharges[addOnCharge.id ?? ''];

        return isAddOnChargeDraftChanged(addOnCharge, draft)
          ? [toAddOnChargeDTO(addOnCharge, draft)]
          : [];
      }),
      ...values.newAddOnCharges.flatMap(toNewAddOnChargeDTO),
    ],
  };
}

export const hasSettlementLinesUpdate = ({
  addOnChargesByInvoiceId,
  lineItemsByInvoiceId,
  settlementAddOnCharges,
}: SettlementLinesUpdate) =>
  addOnChargesByInvoiceId.size > 0 ||
  lineItemsByInvoiceId.size > 0 ||
  settlementAddOnCharges.length > 0;
